import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRole } from "../../context/RoleContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  Typography,
} from "@mui/material";
import {
  mainContentStyle,
  paginationCaptionStyle,
  paginationContainerStyle,
} from "../customerManagement/DisplayCustomers.styles";
import DataTable from "../../common/DataTable";
import {
  addNewButtonStyle,
  cellStyle,
  grandTotalAmountStyle,
  grandTotalStyle,
  labelStyle,
  nameStyle,
  grandTotalMobVer,
  paginationMobVer,
  customerPaginationBoxStyle,
  ledgerCustomerHeaderRowStyle,
  transactionSummaryTextStyle,
  getCardHeaderWrapperStyle,
  downloadLedgerButtonStyle,
  getDataTableHeaderRowStyle,
  getCardContentStyle,
  creditNoteButtonStyle,
  sellerLedgerTitleRowStyle,
  sellerLedgerCustomerSelectorWrapStyle,
  sellerLedgerCustomerLabelStyle,
  sellerLedgerCustomerSelectStyle,
  sellerLedgerTopControlsStyle,
  sellerLedgerLeftControlsStyle,
  sellerLedgerRightControlsStyle,
  sellerDownloadIconButtonStyle,
  sellerDownloadIconStyle,
} from "./ledgerStyles";
import AddIcon from "@mui/icons-material/Add";
import downloadIcon from "../../assets/DownloadTransactionSummaryIcon.svg";
import downloadIconCP from "../../assets/download-ledgerIcon-cp.svg";
import creditNoteIcon from "../../assets/CreditNoteIcon.svg";
import SortBy from "../../common/filters/SortBy";
import CustomDateRangePicker from "../../pages/selller/dashboards/components/custom-date-range";
import { useAppSelector } from "../../redux/hooks";
import SearchBar from "../../common/filters/SearchBar";
import moment from "moment";
import { downloadLedger, getLedgerList } from "../../api/ledgerAPI";
import { LedgerResponseType, SelectedEntryType } from "../../constants/types";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchCustomers, getCustomerById } from "../../api/customerAPI";
import { LedgerModal } from "./LedgerModal";
import useIsMobile from "../../common/mobileVersion/useIsMobile";
import CreditNoteModal from "./CreditNoteModal";
import Toast from "../../common/Toast";
import { exportToPdf } from "../../utils/exportToPdf";
import { exportToExcel } from "../../utils/exportToExcel";
import DownloadCreditNoteModal from "./DownloadCreditNoteModal";
import { useOutsideClose } from "../../hooks/useOutsideClose";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Breadcrumbs from "../../common/Breadcrumbs";
import EditIcon from "@mui/icons-material/Edit";
import colors from "../../theme/Colours";

const Ledger = () => {
  type CustomerOption = {
    id: string;
    outletName: string;
  };

  const { role } = useRole();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const customer = useAppSelector((state) => state.app.customerData?.customer);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const isSeller =
    role === "maltora-seller" ||
    location.pathname.startsWith("/maltora-seller");
  const [showDownloadCNModal, setShowDownloadCNModal] = useState(false);
  const [modalMode, setModalMode] = useState<"ledger" | "creditNote" | null>(
    null
  );
  const [open, setOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<SelectedEntryType | null>(
    null
  );
  const [resturantName, setResturantName] = useState<string | undefined>(
    undefined
  );
  const [customersList, setCustomersList] = useState<CustomerOption[]>([]);
  const isSalesRep =
    role === "maltora-sales-rep" ||
    location.pathname.startsWith("/maltora-sales-rep");

  const customerId = useMemo(() => {
    const pathParts = location.pathname.split("/");
    if ((isSeller || isSalesRep) && pathParts.includes("ledgers")) {
      return pathParts.at(-1);
    }

    return customer?.id;
  }, [isSeller, isSalesRep, location.pathname, customer?.id]);

  const orgId = localStorage.getItem("orgId");
  const isSellerLedgerRoute =
    isSeller && /\/maltora-seller\/ledgers\//.test(location.pathname);
  const breadcrumbPaths = ["Back to Customers", "Transaction Summary"];
  const breadcrumbLinks = ["/maltora-seller/all-customers", "#"];

  const columns = [
    { label: "Date", field: "date" },
    { label: "Order ID", field: "orderId" },
    ...(!isSeller ? [{ label: "Credit Note ID", field: "creditNoteId" }] : []),
    { label: "Debit", field: "debit" },
    { label: "Credit", field: "credit", align: "center" as const },
  ];

  const options = ["Newest", "Oldest"];
  const [sortBy, setSortBy] = useState(options[0]);

  const [from, setFrom] = useState<Date | undefined>(undefined);
  const [to, setTo] = useState<Date | undefined>(undefined);

  const [openFilter, setOpenFilter] = React.useState<"sort" | "date" | null>(
    null
  );
  const filterContainerRef = React.useRef<HTMLDivElement>(null);

  useOutsideClose(filterContainerRef, openFilter !== null, () =>
    setOpenFilter(null)
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [ledgerData, setLedgerData] = useState<LedgerResponseType | undefined>(
    undefined
  );

  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    totalRows: 0,
    totalPages: 1,
  });

  const [cnData, setCnData] = useState<any>(null);
  const handleIssueCreditNote = () => {
    if (!customerId) {
      setToastMessage("No customerId found");
      setIsError(true);
      setShowToast(true);
      return;
    }
    setCnData({ customerId });
    setModalMode("creditNote");
    setOpen(true);
  };

  const handleEdit = (item: any) => {
    if (item.entryType === "creditNote" || item.type === "CREDIT_NOTE") {
      setModalMode("creditNote");
      setCnData({
        customerId,
        creditNoteNo: item.id,
        date: item.dispatchDate || item.createdAt,
        credit: item.credit,
      });
    } else {
      setSelectedEntry({
        id: item.id,
        orderId: item.orderId,
        creditAmount: item.credit,
        date: item.debit ? item.dispatchDate : item.createdAt,
      });
      setModalMode("ledger");
    }
    setOpen(true);
  };

  const sortedLedgerEntries = React.useMemo(() => {
    const entries = [...(ledgerData?.data ?? [])];
    if (!isSeller) return entries;

    const direction = sortBy === "Newest" ? -1 : 1;

    const getDayValue = (item: any) => {
      const raw = item.debit ? item.dispatchDate : item.createdAt;
      if (!raw) return 0;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return 0;
      return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    };

    const getCreatedAtValue = (item: any) => {
      const t = item.createdAt ? new Date(item.createdAt).getTime() : 0;
      return isNaN(t) ? 0 : t;
    };

    const isCreditEntry = (item: any) =>
      item.type === "CREDIT_NOTE" || (!item.debit && item.credit != null);

    // Credit notes from the API often share the same calendar-day createdAt
    // (e.g. "2026-05-25T00:00:00.000Z"), so use the trailing numeric suffix of
    // the credit note id (e.g. CN/SER/26-00005) to determine creation order.
    const getCreditNoteSeq = (item: any) => {
      const raw = item.creditNoteId || item.id || "";
      const match = String(raw).match(/(\d+)(?!.*\d)/);
      return match ? parseInt(match[1], 10) : 0;
    };

    return entries.sort((a, b) => {
      // Primary: calendar day (ignore time-of-day so a debit's late
      // dispatchDate time doesn't outrank a same-day credit note)
      const dayDiff = getDayValue(b) - getDayValue(a);
      if (dayDiff !== 0) return direction === -1 ? dayDiff : -dayDiff;

      // Same day: credit notes appear above debit entries
      const aCredit = isCreditEntry(a) ? 1 : 0;
      const bCredit = isCreditEntry(b) ? 1 : 0;
      if (aCredit !== bCredit) return bCredit - aCredit;

      // Same day and both credit notes: use the trailing numeric suffix of
      // the credit note id as a reliable creation-order tie-breaker.
      if (aCredit === 1 && bCredit === 1) {
        const seqDiff = getCreditNoteSeq(b) - getCreditNoteSeq(a);
        if (seqDiff !== 0) return direction === -1 ? seqDiff : -seqDiff;
      }

      // Same day and same type: order by createdAt according to sort direction
      const createdDiff = getCreatedAtValue(b) - getCreatedAtValue(a);
      return direction === -1 ? createdDiff : -createdDiff;
    });
  }, [ledgerData?.data, isSeller, sortBy]);

  const tableData =
    sortedLedgerEntries.map((item) => {
      const isCreditNote = item.type === "CREDIT_NOTE";

      const hasCreditValue = item.credit !== undefined && item.credit !== null;
      const showEditableCredit =
        isCreditNote || (!item.debit && hasCreditValue);

      const creditCell = showEditableCredit ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap="6px"
        >
          <Box
            sx={{
              border: "1px solid #d0d0d0",
              borderRadius: "4px",
              px: "10px",
              py: "4px",
              minWidth: "100px",
              textAlign: "center",
              fontSize: "14px",
              color: "#333",
              backgroundColor: "#fff",
            }}
          >
            {item.credit}
          </Box>
          {isSeller && (
            <IconButton
              size="small"
              onClick={() =>
                handleEdit(
                  isCreditNote ? { ...item, entryType: "creditNote" } : item
                )
              }
              sx={{
                backgroundColor: "#FFF3E0",
                borderRadius: "50%",
                width: 28,
                height: 28,
                "&:hover": { backgroundColor: "#FFE0B2" },
              }}
            >
              <EditIcon
                sx={{ fontSize: 14, color: colors.primaryButtonOrange }}
              />
            </IconButton>
          )}
        </Box>
      ) : (
        <Typography component="span">{item.credit}</Typography>
      );

      return {
        date: moment(item.debit ? item.dispatchDate : item.createdAt).format(
          "DD MMM YYYY"
        ),
        orderId: isCreditNote
          ? item.orderIds && item.orderIds.length > 0
            ? item.orderIds.join(", ")
            : (item.orderId ?? "-")
          : item.debit
            ? item.id
            : (item.orderId ?? item.id),
        creditNoteId: isCreditNote
          ? (item.creditNoteId ?? item.id ?? "-")
          : "-",
        debit: item.debit,
        credit: creditCell,
      };
    }) ?? [];

  const handleDownload = async (type: "ALL" | "LEDGER" | "CREDIT_NOTE") => {
    const { rowsPerPage, page } = pagination;
    const queryParams = {
      customerId: customerId ?? "",
      page,
      rowsPerPage,
      sort: sortBy === "Newest" ? "desc" : "asc",
      startDate: (from && moment(from).format("YYYY-MM-DD")) ?? "",
      endDate: (to && moment(to).format("YYYY-MM-DD")) ?? "",
    };

    try {
      const response = await downloadLedger({
        ...queryParams,
        page: queryParams.page.toString(),
        rowsPerPage: queryParams.rowsPerPage.toString(),
      });

      if (!response?.data?.length) {
        console.error("No data available to download.");
        return;
      }

      let filtered = response.data;

      // filter by type
      if (type === "CREDIT_NOTE") {
        filtered = filtered.filter((e: any) => e.type === "CREDIT_NOTE");
      }

      if (!filtered.length) {
        console.error(`No ${type.toLowerCase()} entries found.`);
        return;
      }

      if (!isSeller) {
        exportToPdf(type, filtered);
      } else if (isSeller && type === "ALL") {
        const excelData = filtered.map((entry: any) => {
          const isCreditNote = entry.type === "CREDIT_NOTE";
          return {
            Date: moment(entry.dispatchDate || entry.createdAt).format(
              "DD MMM YYYY"
            ),
            "Reference ID": isCreditNote
              ? entry.id
              : entry.orderId || entry.id || "N/A",
            Debit: entry.debit || "0.00",
            Credit: entry.credit || "0.00",
            Type: entry.type || "LEDGER",
          };
        });
        await exportToExcel(excelData, "ledger-entries.xlsx");
      }
    } catch (err) {
      console.error(`Failed to download ${type.toLowerCase()}`, err);
    }
  };

  const fetchLedgerData = async (page: string, limit: string) => {
    const queryParams = {
      customerId: customerId ?? "",
      page,
      limit: isMobile ? "1000" : "10",
      sort: sortBy === "Newest" ? "desc" : "asc",
      startDate: (from && moment(from).format("YYYY-MM-DD")) ?? "",
      endDate: (from && moment(to).format("YYYY-MM-DD")) ?? "",
      search: debouncedSearchTerm,
    };

    try {
      const response = await getLedgerList(queryParams);
      setLedgerData(response);

      if (!isMobile && response?.pagination) {
        setPagination({
          page: Number(response.pagination.currentPage) || 1,
          rowsPerPage: 10,
          totalRows: Number(response.pagination.totalItems) || 1,
          totalPages: Number(response.pagination.totalPages) || 1,
        });
      }
    } catch (err) {
      console.error("Failed to fetch ledger data", err);
    }
  };

  const fetchCustomer = async () => {
    if (isSeller && customerId && !resturantName) {
      try {
        const response = await getCustomerById(customerId);
        setResturantName(response.data.data.customer.outletName);
      } catch (err) {
        console.error("Failed to fetch customer", err);
      }
    }
  };

  const loadCustomersList = useCallback(async () => {
    if (!isSellerLedgerRoute) return;

    try {
      const response = await fetchCustomers({
        page: 1,
        limit: 1000,
        search: "",
      });

      const customers = (response?.data?.data?.customers ??
        []) as CustomerOption[];
      const mappedCustomers = customers.map((item) => ({
        id: item.id,
        outletName: item.outletName,
      }));
      setCustomersList(mappedCustomers);
    } catch (error) {
      console.error("Failed to load customers list", error);
    }
  }, [isSellerLedgerRoute]);

  const handleCustomerSelect = (event: SelectChangeEvent<string>) => {
    const selectedCustomerId = event.target.value;
    if (!selectedCustomerId || selectedCustomerId === customerId) return;
    navigate(`/maltora-seller/ledgers/${selectedCustomerId}`);
  };

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm || (debouncedSearchTerm === "" && customerId)) {
      fetchLedgerData("1", pagination.rowsPerPage.toString());
    }
  }, [debouncedSearchTerm, customerId]);

  useEffect(() => {
    fetchCustomer();
  }, [customerId, isSeller]);

  useEffect(() => {
    loadCustomersList();
  }, [loadCustomersList]);

  useEffect(() => {
    if (!isSeller && customer?.outletName) {
      setResturantName(customer.outletName);
    }
  }, [isSeller, customer?.outletName]);

  useEffect(() => {
    if (customerId) {
      const rows = isMobile
        ? (ledgerData?.data?.length ?? 10).toString()
        : pagination.rowsPerPage.toString();
      fetchLedgerData("1", rows);
      setPagination((prev) => ({ ...prev, page: 1 })); // reset to page 1
    }
  }, [sortBy]);

  useEffect(() => {
    if (!customerId) return;

    const rows = isMobile
      ? (ledgerData?.data?.length ?? 10).toString()
      : pagination.rowsPerPage.toString();

    fetchLedgerData("1", rows);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [from, to]);

  useEffect(() => {
    if (from === undefined && to === undefined && customerId) {
      fetchLedgerData("1", pagination.rowsPerPage.toString());
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [from, to, customerId, pagination.rowsPerPage]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearchTerm(searchValue);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleChangePage = (newPage: number) => {
    if (!isMobile) {
      setPagination((prev) => ({ ...prev, page: newPage }));
      fetchLedgerData(newPage.toString(), pagination.rowsPerPage.toString());
    }
  };

  const { page, rowsPerPage, totalPages, totalRows } = pagination;
  const startIndex = (+page - 1) * +rowsPerPage;
  const endIndex = Math.min(startIndex + +rowsPerPage, +totalRows);

  const difference =
    ledgerData &&
    ledgerData.totalDebitAmount !== undefined &&
    ledgerData.totalCredit !== undefined &&
    ledgerData.totalCreditNoteAmount !== undefined
      ? +ledgerData.totalCredit +
        +ledgerData.totalCreditNoteAmount -
        +ledgerData.totalDebitAmount
      : 0;

  const grandTotalBlock = (
    <Table>
      <TableBody>
        {[
          { sx: grandTotalStyle, value: "TOTAL OUTSTANDING" },
          {},
          {},
          {
            sx: grandTotalAmountStyle(difference >= 0), // true = green, false = red
            value: Math.abs(difference),
          },
        ].map((item, idx) => (
          <TableCell
            key={`grandTotal_${idx}`}
            sx={{
              ...cellStyle("", isMobile, isSeller),
              width: "26%",
              borderBottom: { xs: "none" },
            }}
          >
            <Typography sx={item.sx}>{item.value}</Typography>
          </TableCell>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <>
      {/* Header Section */}
      <Box sx={ledgerCustomerHeaderRowStyle}>
        {isSellerLedgerRoute && (
          <Breadcrumbs paths={breadcrumbPaths} links={breadcrumbLinks} />
        )}
        <Box sx={sellerLedgerTitleRowStyle}>
          <Typography variant="h5" sx={transactionSummaryTextStyle}>
            Transaction Summary
          </Typography>
          {isSellerLedgerRoute && (
            <Box sx={sellerLedgerCustomerSelectorWrapStyle}>
              <Typography sx={sellerLedgerCustomerLabelStyle}>
                Customer:
              </Typography>
              <Select
                value={customerId ?? ""}
                onChange={handleCustomerSelect}
                IconComponent={KeyboardArrowDownIcon}
                sx={sellerLedgerCustomerSelectStyle}
                displayEmpty
              >
                {customersList.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.outletName}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
        </Box>
      </Box>
      <Card>
        <CardContent sx={getCardContentStyle(isSeller)}>
          {isSeller && (
            <Box sx={sellerLedgerTopControlsStyle}>
              <Box
                sx={{
                  ...sellerLedgerLeftControlsStyle,
                  position: "relative",
                  zIndex: openFilter === "date" ? 998 : undefined,
                }}
              >
                {!isMobile && (
                  <SearchBar
                    handleSearch={handleSearch}
                    searchTerm={searchTerm}
                  />
                )}
                <SortBy
                  selected={sortBy}
                  options={options}
                  setSelected={setSortBy}
                  variant="seller"
                  hideOptions
                  isOpen={openFilter === "sort"}
                  onToggle={(open) => setOpenFilter(open ? "sort" : null)}
                />
                <CustomDateRangePicker
                  fromDate={from}
                  toDate={to}
                  setFromDate={setFrom}
                  setToDate={setTo}
                  placeholder="Select date range"
                  disableFuture
                  showReset
                  sx={{
                    height: "38px",
                    width: "220px",
                  }}
                />
              </Box>

              <Box sx={sellerLedgerRightControlsStyle}>
                <Button
                  startIcon={
                    <img src={creditNoteIcon} alt="credit note" height="20px" />
                  }
                  variant="outlined"
                  sx={creditNoteButtonStyle}
                  onClick={handleIssueCreditNote}
                >
                  Issue Credit Note
                </Button>
                {/* <Button
                  startIcon={<AddIcon height="14px" />}
                  variant="outlined"
                  sx={addNewButtonStyle}
                  onClick={() => {
                    setSelectedEntry(null);
                    setModalMode("ledger");
                    setOpen(true);
                  }}
                >
                  Add Credit
                </Button> */}
                <IconButton
                  onClick={() => handleDownload("ALL")}
                  sx={sellerDownloadIconButtonStyle}
                >
                  <Box
                    component="img"
                    src={downloadIcon}
                    alt="download"
                    sx={sellerDownloadIconStyle}
                  />
                </IconButton>
              </Box>
            </Box>
          )}
          {!isSeller && (
            <Box sx={getCardHeaderWrapperStyle(isSeller)}>
              <Typography sx={nameStyle}>{resturantName}</Typography>
              <Box display="flex" gap="25px">
                <>
                  <Button
                    onClick={() => setShowDownloadCNModal(true)}
                    variant="outlined"
                    startIcon={
                      <Box
                        component="img"
                        src={downloadIconCP}
                        alt="download"
                        sx={{ width: "14px", height: "14px" }}
                      />
                    }
                    sx={downloadLedgerButtonStyle}
                  >
                    Download Credit Note
                  </Button>
                  <Button
                    onClick={() => handleDownload("LEDGER")}
                    variant="outlined"
                    startIcon={
                      <Box
                        component="img"
                        src={downloadIconCP}
                        alt="download"
                        sx={{ width: "14px", height: "14px" }}
                      />
                    }
                    sx={downloadLedgerButtonStyle}
                  >
                    Download Ledger
                  </Button>
                </>
              </Box>
            </Box>
          )}

          {/* Table Records */}
          <Box sx={mainContentStyle} mt="16px">
            <DataTable
              columns={columns.map((col) => ({
                ...col,
                labelStyle: labelStyle(isMobile, isSeller),
                cellStyle: cellStyle(col.field, isMobile, isSeller),
              }))}
              data={tableData}
              emptyStateMessage="No records available"
              headerRowStyle={getDataTableHeaderRowStyle(isSeller)}
            />
          </Box>

          {/* Grand Total */}
          <Box>{!isMobile && grandTotalBlock}</Box>
          {!isMobile && role !== "maltora-customer" && <Divider />}

          {/* Pagination */}
          {!isMobile && (
            <Box
              sx={
                role === "maltora-customer"
                  ? customerPaginationBoxStyle
                  : paginationContainerStyle
              }
            >
              <Typography variant="body2" sx={paginationCaptionStyle}>
                Showing data {startIndex + 1} to {endIndex} of {totalRows}{" "}
                entries
              </Typography>
              <Pagination
                count={totalPages}
                page={+page}
                onChange={(_, newPage) => handleChangePage(newPage)}
                shape="rounded"
                variant="outlined"
                sx={paginationMobVer(isMobile)}
              />
            </Box>
          )}
        </CardContent>
      </Card>
      {isMobile && <Box sx={grandTotalMobVer}>{grandTotalBlock}</Box>}
      {/* Add/Edit Modal (seller only) */}
      {isSeller && modalMode === "ledger" && (
        <LedgerModal
          open={open}
          onClose={() => setOpen(false)}
          entry={selectedEntry}
          fetchLedgerData={fetchLedgerData}
          customerId={customerId ?? ""}
        />
      )}
      {isSeller && modalMode === "creditNote" && (
        <CreditNoteModal
          open={open}
          onClose={() => setOpen(false)}
          fetchLedgerData={fetchLedgerData}
          rowsPerPage={pagination.rowsPerPage}
          customerId={cnData?.customerId}
          mode={cnData?.creditNoteNo ? "edit" : "create"}
          creditNoteData={
            cnData?.creditNoteNo && cnData?.date
              ? {
                  creditNoteNo: cnData.creditNoteNo,
                  date: cnData.date,
                  credit: cnData.credit,
                }
              : undefined
          }
        />
      )}
      {/* Download Credit Note Modal */}
      <DownloadCreditNoteModal
        open={showDownloadCNModal}
        onClose={() => setShowDownloadCNModal(false)}
      />
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={isError}
      />
    </>
  );
};
export default Ledger;
